import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import { getHistoricalReport } from '../../../api/historicalReportsApi';
import { downloadReport, getReportSummary } from '../../../api/reportsApi';
import { ApiError } from '../../../shared/api';
import {
  isHistoricalViewerYear, reportingCalendarYear,
} from '../../../shared/utils/reportingYear';
import {
  emptyReportSummary,
  normalizeHistoricalReportSummary,
  normalizeReportSummary,
  REPORT_EXPORT_MODES,
  selectableReportingYears,
} from '../model/reportModel';

const isCanceled = error => (
  error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError'
);

export default function useReportsMaster() {
  const [requestedYear, setRequestedYear] = useState(reportingCalendarYear());
  const [data, setData] = useState(() => emptyReportSummary());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [reloadSequence, setReloadSequence] = useState(0);
  const sequenceRef = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    const sequence = sequenceRef.current + 1;
    sequenceRef.current = sequence;
    setData(emptyReportSummary(requestedYear));
    setLoading(true);
    setError(null);

    const isHistorical = isHistoricalViewerYear(requestedYear);
    const request = isHistorical
      ? getHistoricalReport(requestedYear, { signal: controller.signal })
      : getReportSummary(requestedYear, { signal: controller.signal });

    request
      .then((payload) => {
        if (sequenceRef.current !== sequence) return;
        setData(isHistorical
          ? normalizeHistoricalReportSummary(payload, requestedYear)
          : normalizeReportSummary(payload, requestedYear));
      })
      .catch((loadError) => {
        if (sequenceRef.current !== sequence || isCanceled(loadError)) return;
        if (isHistorical && loadError?.isNotFound) {
          setData(emptyReportSummary(requestedYear));
          return;
        }
        setError(loadError);
      })
      .finally(() => {
        if (sequenceRef.current === sequence) setLoading(false);
      });

    return () => controller.abort();
  }, [reloadSequence, requestedYear]);

  const rows = useMemo(() => data.report.rows, [data.report.rows]);

  const exportReport = useCallback(async (
    exportMode = REPORT_EXPORT_MODES.STANDARD,
  ) => {
    const fiscalYear = data.report.year;
    if (isHistoricalViewerYear(fiscalYear)) {
      throw new ApiError('REPORT_EXPORT_NOT_ALLOWED_FOR_HISTORICAL_YEAR', {
        status: 400,
        kind: 'badRequest',
      });
    }
    setExporting(true);
    try {
      const result = await downloadReport(fiscalYear, exportMode);
      return {
        ...result,
        href: URL.createObjectURL(result.blob),
        sizeBytes: result.blob.size,
        exportMode,
        fiscalYear,
      };
    } finally {
      setExporting(false);
    }
  }, [data.report.year]);

  const setYear = useCallback((nextYear) => {
    setRequestedYear(Number(nextYear));
  }, []);

  const reload = useCallback(() => {
    setReloadSequence(sequence => sequence + 1);
  }, []);

  return {
    years: selectableReportingYears(),
    year: requestedYear ?? data.meta.year,
    setYear,
    reload,
    report: data.report,
    rows,
    exporting,
    exportReport,
    loading,
    error,
    meta: data.meta,
    historicalReport: data.historical || null,
  };
}
